// Authentication utility functions

/**
 * Check if a JWT token is valid (not expired, properly formatted)
 * @param token - JWT token to validate
 * @returns boolean indicating if token is valid
 */
export const isTokenValid = (token: string | null): boolean => {
  if (!token || token === 'null' || token === 'undefined') {
    return false;
  }

  try {
    // Basic JWT format check (should have 3 parts separated by dots)
    const parts = token.split('.');
    if (parts.length !== 3) {
      return false;
    }

    // Decode payload to check expiration
    const payload = JSON.parse(atob(parts[1]));
    const currentTime = Math.floor(Date.now() / 1000);
    
    // Check if token is expired
    if (payload.exp && payload.exp < currentTime) {
      return false;
    }

    return true;
  } catch (error) {
    console.error('Token validation error:', error);
    return false;
  }
};

/**
 * Get and validate authentication token from localStorage
 * @returns Valid token or null
 */
export const getValidToken = (): string | null => {
  const token = localStorage.getItem('authToken');
  
  if (!isTokenValid(token)) {
    // Clean up invalid token
    clearAuthData();
    return null;
  }
  
  return token;
};

/**
 * Clear authentication data from localStorage
 */
export const clearAuthData = (): void => {
  localStorage.removeItem('authToken');
  localStorage.removeItem('authUser');
};

/**
 * Check if user is authenticated with a valid token
 * @returns boolean indicating if user is authenticated
 */
export const isAuthenticated = (): boolean => {
  return getValidToken() !== null;
};

/**
 * Extract user ID from JWT token
 * @param token - JWT token
 * @returns User ID or null
 */
export const getUserIdFromToken = (token: string): string | null => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    const payload = JSON.parse(atob(parts[1]));
    return payload.userId || null;
  } catch (error) {
    console.error('Error extracting user ID from token:', error);
    return null;
  }
};